/**
 * 标签页管理 Store
 * 管理打开的标签页列表，类似浏览器多标签功能
 */
import { defineStore } from 'pinia'

export const useTabsStore = defineStore('tabs', {
  state: () => ({
    // 已访问的视图列表（打开的标签页）
    visitedViews: [],
    // 缓存的视图列表（用于 keep-alive）
    cachedViews: []
  }),

  getters: {
    // 获取所有标签页
    getVisitedViews: (state) => state.visitedViews,

    // 获取需要缓存的组件名称列表
    getCachedViews: (state) => state.cachedViews
  },

  actions: {
    /**
     * 添加视图到标签页
     * @param {Object} view - 路由视图对象
     */
    addView(view) {
      this.addVisitedView(view)
      this.addCachedView(view)
    },

    /**
     * 添加已访问的视图
     */
    addVisitedView(view) {
      // 检查是否已存在
      const existingIndex = this.visitedViews.findIndex(v => v.path === view.path)

      if (existingIndex !== -1) {
        // 已存在，更新视图信息（确保标题总是最新的）
        this.visitedViews[existingIndex] = {
          ...this.visitedViews[existingIndex],
          path: view.path,
          name: view.name,
          title: view.meta?.title || '未命名页面',
          query: view.query || {},
          params: view.params || {},
          affix: view.meta?.affix || false,
          icon: view.meta?.icon || null
        }
      } else {
        // 不存在，添加新标签
        this.visitedViews.push({
          path: view.path,
          name: view.name,
          title: view.meta?.title || '未命名页面',
          query: view.query || {},
          params: view.params || {},
          affix: view.meta?.affix || false, // 固定标签（不可关闭）
          icon: view.meta?.icon || null
        })
      }
    },

    /**
     * 添加到缓存列表
     */
    addCachedView(view) {
      // 如果路由配置了不缓存，则跳过
      if (view.meta?.noCache) {
        return
      }

      const componentName = view.name
      if (componentName && !this.cachedViews.includes(componentName)) {
        this.cachedViews.push(componentName)
      }
    },

    /**
     * 移除视图（关闭标签）
     * @param {Object} view - 要移除的视图
     */
    removeView(view) {
      if (!view) return
      this.removeVisitedView(view)
      this.removeCachedView(view)
    },

    /**
     * 从已访问列表中移除
     */
    removeVisitedView(view) {
      if (!view || !view.path) return
      const index = this.visitedViews.findIndex(v => v.path === view.path)
      if (index !== -1) {
        this.visitedViews.splice(index, 1)
      }
    },

    /**
     * 从缓存列表中移除
     */
    removeCachedView(view) {
      if (!view || !view.name) return
      const componentName = view.name
      const index = this.cachedViews.indexOf(componentName)
      if (index !== -1) {
        this.cachedViews.splice(index, 1)
      }
    },

    /**
     * 关闭其他标签页（保留当前和固定标签）
     * @param {Object} view - 当前视图
     */
    closeOtherViews(view) {
      if (!view || !view.path) return

      this.visitedViews = this.visitedViews.filter(
        v => v.affix || v.path === view.path
      )

      // 更新缓存列表
      this.cachedViews = this.visitedViews
        .filter(v => v.name && !v.meta?.noCache)
        .map(v => v.name)
    },

    /**
     * 关闭所有标签页（保留固定标签）
     */
    closeAllViews() {
      this.visitedViews = this.visitedViews.filter(v => v.affix)

      // 更新缓存列表
      this.cachedViews = this.visitedViews
        .filter(v => v.name && !v.meta?.noCache)
        .map(v => v.name)
    },

    /**
     * 关闭左侧所有标签
     * @param {Object} view - 当前视图
     */
    closeLeftViews(view) {
      if (!view || !view.path) return

      const index = this.visitedViews.findIndex(v => v.path === view.path)
      if (index === -1) return

      this.visitedViews = this.visitedViews.filter((v, i) => {
        return v.affix || i >= index
      })

      this.updateCachedViews()
    },

    /**
     * 关闭右侧所有标签
     * @param {Object} view - 当前视图
     */
    closeRightViews(view) {
      if (!view || !view.path) return

      const index = this.visitedViews.findIndex(v => v.path === view.path)
      if (index === -1) return

      this.visitedViews = this.visitedViews.filter((v, i) => {
        return v.affix || i <= index
      })

      this.updateCachedViews()
    },

    /**
     * 更新缓存视图列表
     */
    updateCachedViews() {
      this.cachedViews = this.visitedViews
        .filter(v => v.name && !v.meta?.noCache)
        .map(v => v.name)
    },

    /**
     * 清空所有标签页（重置状态）
     */
    clearAllViews() {
      this.visitedViews = []
      this.cachedViews = []
    }
  },

  // 持久化配置 - 刷新页面后保留标签页
  persist: {
    key: 'saas-tabs', // 存储key
    storage: localStorage, // 使用localStorage
    paths: ['visitedViews'] // 只持久化访问记录，不持久化缓存列表
  }
})
